import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { deliveryFeeService } from '../../services/deliveryFeeService';
import { DeliveryFeeTier, DeliveryFeeTierCreate } from '../../types/deliveryFee';
import { useToast } from '../../hooks/use-toast';

interface Props {
    configId: number;
    onClose: () => void;
}

const DeliveryFeeTierEditor: React.FC<Props> = ({ configId, onClose }) => {
    const { toast } = useToast();
    const [tiers, setTiers] = useState<DeliveryFeeTier[]>([]);
    const [loading, setLoading] = useState(true);
    const [newTier, setNewTier] = useState<DeliveryFeeTierCreate>({
        min_value: 0,
        max_value: undefined,
        fee: 0
    });

    useEffect(() => {
        loadTiers();
    }, [configId]);

    const loadTiers = async () => {
        try {
            setLoading(true);
            const config = await deliveryFeeService.getById(configId);
            setTiers(config.tiers || []);
        } catch (error) {
            toast({
                title: 'Erreur',
                description: 'Impossible de charger les paliers',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddTier = async () => {
        try {
            await deliveryFeeService.createTier(configId, newTier);
            toast({
                title: 'Succès',
                description: 'Palier ajouté'
            });
            setNewTier({ min_value: 0, max_value: undefined, fee: 0 });
            loadTiers();
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.response?.data?.detail || 'Erreur lors de l\'ajout',
                variant: 'destructive'
            });
        }
    };

    const handleDeleteTier = async (tierId: number) => {
        if (!confirm('Supprimer ce palier ?')) return;

        try {
            await deliveryFeeService.deleteTier(configId, tierId);
            toast({
                title: 'Succès',
                description: 'Palier supprimé'
            });
            loadTiers();
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.response?.data?.detail || 'Erreur lors de la suppression',
                variant: 'destructive'
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold">Paliers de Tarification</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Existing Tiers */}
                    <div>
                        <h3 className="font-medium text-gray-900 mb-3">Paliers Existants</h3>
                        {loading ? (
                            <div className="text-center py-8 text-gray-500">Chargement...</div>
                        ) : tiers.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                Aucun palier défini
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {tiers.map((tier) => (
                                    <div
                                        key={tier.id}
                                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-300"
                                    >
                                        <div className="flex items-center gap-4 text-sm">
                                            <span className="font-medium text-gray-900">
                                                {tier.min_value} {tier.max_value ? `- ${tier.max_value}` : '+'}
                                            </span>
                                            <span className="text-gray-500">→</span>
                                            <span className="font-semibold text-blue-600">
                                                {tier.fee.toFixed(2)}€
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteTier(tier.id)}
                                            className="text-red-600 hover:bg-red-50 p-2 rounded"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Tier */}
                    <div className="border-t pt-6">
                        <h3 className="font-medium text-gray-900 mb-3">Ajouter un Palier</h3>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valeur Min
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={newTier.min_value}
                                    onChange={(e) => setNewTier({ ...newTier, min_value: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Valeur Max
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={newTier.max_value || ''}
                                    onChange={(e) => setNewTier({ ...newTier, max_value: parseFloat(e.target.value) || undefined })}
                                    placeholder="Illimité"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Frais (€)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={newTier.fee}
                                    onChange={(e) => setNewTier({ ...newTier, fee: parseFloat(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleAddTier}
                            className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" />
                            Ajouter le Palier
                        </button>
                    </div>

                    {/* Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                        <p className="font-medium mb-1">💡 Comment ça marche ?</p>
                        <p>
                            Définissez des tranches de valeurs (distance ou poids) avec un tarif fixe pour chaque tranche.
                            Laissez "Valeur Max" vide pour la dernière tranche (illimitée).
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryFeeTierEditor;
